/* eslint-disable no-unused-vars */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  Building2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ArrowLeft,
  Plus,
  RefreshCw,
  Archive,
  ExternalLink,
  Loader2,
  HardHat,
  ShieldCheck,
  Hourglass,
  FileSignature,
  FolderArchive,
  FileText,
  Upload,
  Scale,
  Calculator,
} from 'lucide-react';



export const CcmiCreateDialog = ({ ctx }) => {
  const {
    analyzingDocId, archiving, assigningJuriste, autosaveAt, autosaveError, autosaveStatus,
    autosaveTick, complianceDraft, complianceLoading, complianceSaving, compliancingDoc, counts,
    creating, creatingFinalDevis, draft, filterStatut, finalContractPaywall,
    generatingFinalContract, generatingPdf, importingPdf, items, juristeAssignLoading,
    juristeCommentInput, juristeEmailInput, loadError, loading, reportingDocId, reviewingDocId,
    search, selected, showCreate, syncing,
    setAnalyzingDocId, setArchiving, setAssigningJuriste, setAutosaveAt, setAutosaveError,
    setAutosaveStatus, setAutosaveTick, setComplianceDraft, setComplianceLoading, setComplianceSaving,
    setCompliancingDoc, setCounts, setCreating, setCreatingFinalDevis, setDraft,
    setFilterStatut, setFinalContractPaywall, setGeneratingFinalContract, setGeneratingPdf,
    setImportingPdf, setItems, setJuristeAssignLoading, setJuristeCommentInput, setJuristeEmailInput,
    setLoadError, setLoading, setReportingDocId, setReviewingDocId, setSearch, setSelected,
    setShowCreate, setSyncing,
    autosaveBaselineRef, autosaveTimerRef, fetchList, fetchedOnce, fileInputRef, handleAnalyzeDoc,
    handleArchive, handleCreate, handleCreateFinalContractDevis, handleDownloadReviewReport,
    handleGenerateFinalContract, handleGeneratePdf, handleImportPdf, handleReviewAi, handleSync,
    navigate, openComplianceModal, openJuristeAssign, saveComplianceDraft, submitComplianceDraft,
    submitJuristeAssign, triggerAutosave,
    STATUT_META, GUARD_STATUS_META, getAuthHeader, CCMI_COMPLIANCE_CHECKLIST, computeComplianceStatus,
    ComplianceStatusPanel, AutosaveIndicator,
  } = ctx;
  return (
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl" data-testid="ccmi-create-modal">
          <DialogHeader>
            <DialogTitle>Nouveau projet CCMI</DialogTitle>
            <DialogDescription>
              Saisissez les informations principales. La conformité sera recalculée automatiquement.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Référence (optionnel)</Label>
              <Input
                placeholder="Générée auto si vide (CCMI-XXXX)"
                value={draft.reference}
                onChange={(e) => setDraft({ ...draft, reference: e.target.value })}
              />
            </div>
            <div>
              <Label>Maître d&apos;ouvrage — Nom</Label>
              <Input
                value={draft.maitre_ouvrage.nom}
                onChange={(e) => setDraft({ ...draft, maitre_ouvrage: { ...draft.maitre_ouvrage, nom: e.target.value } })}
                data-testid="ccmi-input-mo-nom"
              />
            </div>
            <div>
              <Label>Prénom</Label>
              <Input
                value={draft.maitre_ouvrage.prenom}
                onChange={(e) => setDraft({ ...draft, maitre_ouvrage: { ...draft.maitre_ouvrage, prenom: e.target.value } })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={draft.maitre_ouvrage.email}
                onChange={(e) => setDraft({ ...draft, maitre_ouvrage: { ...draft.maitre_ouvrage, email: e.target.value } })}
              />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input
                value={draft.maitre_ouvrage.telephone}
                onChange={(e) => setDraft({ ...draft, maitre_ouvrage: { ...draft.maitre_ouvrage, telephone: e.target.value } })}
              />
            </div>
            <div>
              <Label>Constructeur — Raison sociale</Label>
              <Input
                value={draft.constructeur.raison_sociale}
                onChange={(e) => setDraft({ ...draft, constructeur: { ...draft.constructeur, raison_sociale: e.target.value } })}
              />
            </div>
            <div>
              <Label>SIRET</Label>
              <Input
                value={draft.constructeur.siret}
                onChange={(e) => setDraft({ ...draft, constructeur: { ...draft.constructeur, siret: e.target.value } })}
              />
            </div>
            <div className="col-span-2">
              <Label>Adresse du terrain</Label>
              <Input
                value={draft.terrain.adresse}
                onChange={(e) => setDraft({ ...draft, terrain: { ...draft.terrain, adresse: e.target.value } })}
              />
            </div>
            <div>
              <Label>Prix TTC (€)</Label>
              <Input
                type="number"
                value={draft.prix_ttc}
                onChange={(e) => setDraft({ ...draft, prix_ttc: e.target.value })}
                data-testid="ccmi-input-prix-ttc"
              />
            </div>
            <div>
              <Label>Type de contrat</Label>
              <Select value={draft.type_contrat} onValueChange={(v) => setDraft({ ...draft, type_contrat: v })}>
                <SelectTrigger data-testid="ccmi-input-type-contrat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="avec_plan">Avec plan</SelectItem>
                  <SelectItem value="sans_plan">Sans plan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea
                rows={2}
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ccmi-submit-create">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
};

export default CcmiCreateDialog;
